import React from 'react';
import { Card, CardContent, CardHeader } from './card';
import { Skeleton } from './skeleton';

const CardIconHeaderSkeleton = ({ titleWidth = 'w-40' }) => (
    <div className="flex items-center gap-3">
        <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
        <Skeleton className={`h-5 ${titleWidth}`} />
    </div>
);

export const TableRowsSkeleton = ({ rows = 5, cols = 5 }) => (
    <div className="space-y-3">
        <div className="flex gap-4">
            {Array.from({ length: cols }).map((_, index) => (
                <Skeleton key={`head-${index}`} className="h-4 flex-1" />
            ))}
        </div>
        {Array.from({ length: rows }).map((_, row) => (
            <div key={row} className="flex gap-4">
                {Array.from({ length: cols }).map((_, col) => (
                    <Skeleton
                        key={col}
                        className={`h-4 flex-1 ${col === cols - 1 ? 'max-w-10' : ''}`}
                    />
                ))}
            </div>
        ))}
    </div>
);

export const TableCardSkeleton = ({ titleWidth = 'w-24', rows = 5, cols = 5, withAction = true }) => (
    <Card>
        <CardHeader>
            <div className="flex items-center justify-between">
                <Skeleton className={`h-6 ${titleWidth}`} />
                {withAction && <Skeleton className="h-9 w-28" />}
            </div>
        </CardHeader>
        <CardContent>
            <TableRowsSkeleton rows={rows} cols={cols} />
        </CardContent>
    </Card>
);

export const ApprovalProcessCardSkeleton = () => (
    <Card>
        <CardHeader>
            <div className="flex items-center justify-between">
                <Skeleton className="h-6 w-36" />
                <Skeleton className="h-5 w-20 rounded-full" />
            </div>
        </CardHeader>
        <CardContent className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-24 w-full rounded-md" />
            <Skeleton className="h-24 w-full rounded-md" />
            <Skeleton className="h-9 w-32" />
        </CardContent>
    </Card>
);

export const BaseCurrencyCardSkeleton = () => (
    <Card>
        <CardHeader>
            <CardIconHeaderSkeleton titleWidth="w-32" />
        </CardHeader>
        <CardContent className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-9 w-32" />
        </CardContent>
    </Card>
);

export const TimezoneCardSkeleton = () => (
    <Card>
        <CardHeader>
            <CardIconHeaderSkeleton titleWidth="w-28" />
        </CardHeader>
        <CardContent className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-9 w-32" />
        </CardContent>
    </Card>
);

export const PaymentExpiryCardSkeleton = () => (
    <Card>
        <CardHeader>
            <CardIconHeaderSkeleton titleWidth="w-36" />
        </CardHeader>
        <CardContent className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-9 w-32" />
        </CardContent>
    </Card>
);

export const AuthSettingsCardSkeleton = () => (
    <Card>
        <CardHeader>
            <Skeleton className="h-6 w-44" />
        </CardHeader>
        <CardContent className="space-y-6">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-10 w-48" />
            <Skeleton className="h-6 w-56" />
            <Skeleton className="h-9 w-24" />
        </CardContent>
    </Card>
);

export const PaymentAuthCardSkeleton = () => (
    <Card className="h-full">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 pb-4">
            <CardIconHeaderSkeleton titleWidth="w-36" />
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-10 w-full max-w-xs" />
            <div className="flex gap-3 pt-2">
                <Skeleton className="h-10 w-28" />
                <Skeleton className="h-10 w-24" />
            </div>
        </CardContent>
    </Card>
);

export const PaymentSummaryCardSkeleton = () => (
    <Card className="h-full">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 pb-4">
            <div className="flex items-center justify-between gap-3">
                <CardIconHeaderSkeleton titleWidth="w-36" />
                <Skeleton className="h-5 w-20 rounded-full" />
            </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="flex items-start gap-3">
                        <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
                        <div className="flex-1 space-y-2">
                            <Skeleton className="h-3 w-20" />
                            <Skeleton className="h-4 w-full" />
                        </div>
                    </div>
                ))}
            </div>
            <div className="pt-4 border-t border-slate-100 space-y-3">
                <Skeleton className="h-3 w-36" />
                <Skeleton className="h-10 w-full max-w-md mx-auto" />
            </div>
        </CardContent>
    </Card>
);

export const ListCardSkeleton = ({ titleWidth = 'w-40', lines = 4 }) => (
    <Card className="h-full">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 pb-4">
            <CardIconHeaderSkeleton titleWidth={titleWidth} />
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
            {Array.from({ length: lines }).map((_, index) => (
                <Skeleton key={index} className={`h-4 ${index % 2 === 0 ? 'w-full' : 'w-5/6'}`} />
            ))}
        </CardContent>
    </Card>
);

export const ApprovalTimelineCardSkeleton = () => (
    <Card className="h-full">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 pb-4">
            <CardIconHeaderSkeleton titleWidth="w-36" />
        </CardHeader>
        <CardContent className="pt-6 space-y-5">
            <Skeleton className="h-3 w-48" />
            {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="flex gap-3">
                    <Skeleton className="h-5 w-5 shrink-0 rounded-full" />
                    <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-48" />
                    </div>
                </div>
            ))}
        </CardContent>
    </Card>
);

export const PaymentDetailsCardSkeleton = () => (
    <Card>
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 pb-4">
            <CardIconHeaderSkeleton titleWidth="w-32" />
        </CardHeader>
        <CardContent className="pt-4">
            <TableRowsSkeleton rows={4} cols={6} />
        </CardContent>
    </Card>
);

export const QRCodeSetupCardSkeleton = () => (
    <Card>
        <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                    <Skeleton className="h-10 w-10 shrink-0 rounded-lg" />
                    <div className="space-y-2">
                        <Skeleton className="h-5 w-48" />
                        <Skeleton className="h-4 w-72" />
                    </div>
                </div>
                <Skeleton className="h-5 w-24 rounded-full" />
            </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
            <div className="flex flex-col sm:flex-row gap-6">
                <Skeleton className="h-40 w-40 shrink-0 rounded-lg" />
                <div className="flex-1 space-y-4">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-11 w-56" />
                </div>
            </div>
        </CardContent>
    </Card>
);

export const WorkflowEditorSkeleton = () => (
    <div className="max-w-5xl mx-auto flex flex-col gap-6">
        <Skeleton className="h-5 w-36" />
        <Card>
            <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <Skeleton className="h-6 w-32" />
                        <Skeleton className="h-5 w-16 rounded-full" />
                    </div>
                    <Skeleton className="h-4 w-48" />
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                <Skeleton className="h-10 w-full max-w-md" />
                <div className="flex gap-2">
                    <Skeleton className="h-9 w-28" />
                    <Skeleton className="h-9 w-24" />
                </div>
            </CardContent>
        </Card>
        <Card>
            <CardHeader>
                <Skeleton className="h-6 w-52" />
            </CardHeader>
            <CardContent className="space-y-4">
                <Skeleton className="h-24 w-full rounded-md" />
                <Skeleton className="h-32 w-full rounded-md" />
                <Skeleton className="h-32 w-full rounded-md" />
            </CardContent>
        </Card>
    </div>
);
